import { Box, Typography, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { HeatMapWidgetProps } from './type';

const defaultHeight = 300;
const getHeightPx = (value: number, heightDifferencePercent: number): string => {
    return `${value + value * (heightDifferencePercent / 100)}px`;
};

interface RangeConfig {
    pieces: Array<{ value: number; label: string; color: string }>;
    tooltipFormatter: (value: number) => string;
}

const getGridLeft = (containerWidth: number): string => {
  if (containerWidth < 420) return '5%';
  if (containerWidth < 920) return '9%';
  if (containerWidth < 1260) return '7%';
  return '5%';
};

const getCalculatedChartHeight = (
  containerWidth: number,
  heightDifferencePercent: number,
  defaultHeight: number
) => {
  if (containerWidth < 420) {
    return getHeightPx(680, heightDifferencePercent);
  }
  if (containerWidth < 500) {
    return getHeightPx(600, heightDifferencePercent);
  }
  if (containerWidth < 685) {
    return getHeightPx(550, heightDifferencePercent);
  }
  if (containerWidth < 920) {
    return getHeightPx(450, heightDifferencePercent);
  }
  return getHeightPx(defaultHeight, heightDifferencePercent);
};


// Define range configurations for different time periods
const getRangeConfig = (timePeriod: 'Daily' | 'Weekly' | 'Monthly'): RangeConfig => {  
    switch (timePeriod) {
        case 'Daily':
            return {
                pieces: [
                    { value: 0, label: 'No Usage', color: '#A9A9A9' },
                    { value: 1, label: 'Low Usage (1-10 Views)', color: '#FFD700' },
                    { value: 2, label: 'Medium Usage (11-50 Views)', color: '#FFA500' },
                    { value: 3, label: 'High Usage (50+ Views)', color: '#FF0000' }
                ],
                tooltipFormatter: (value: number) => {
                    if (value === 0) return 'No Usage';
                    if (value === 1) return 'Low Usage (1-10 Views)';
                    if (value === 2) return 'Medium Usage (11-50 Views)';
                    if (value === 3) return 'High Usage (50+ Views)';
                    return 'No Usage';
                }
            };
        case 'Weekly':
            return {
                pieces: [
                    { value: 0, label: 'No Usage', color: '#A9A9A9' },
                    { value: 1, label: 'Low Usage (1-50 Views)', color: '#FFD700' },
                    { value: 2, label: 'Medium Usage (51-250 Views)', color: '#FFA500' },
                    { value: 3, label: 'High Usage (250+ Views)', color: '#FF0000' }
                ],
                tooltipFormatter: (value: number) => {
                    if (value === 0) return 'No Usage';
                    if (value === 1) return 'Low Usage (1-50 Views)';
                    if (value === 2) return 'Medium Usage (51-250 Views)';
                    if (value === 3) return 'High Usage (250+ Views)';
                    return 'No Usage';
                }
            };
        case 'Monthly':
            return {
                pieces: [
                    { value: 0, label: 'No Usage', color: '#A9A9A9' },
                    { value: 1, label: 'Low Usage (1-250 Views)', color: '#FFD700' },
                    { value: 2, label: 'Medium Usage (251-1250 Views)', color: '#FFA500' },
                    { value: 3, label: 'High Usage (1250+ Views)', color: '#FF0000' }
                ],
                tooltipFormatter: (value: number) => {
                    if (value === 0) return 'No Usage';
                    if (value === 1) return 'Low Usage (1-250 Views)';
                    if (value === 2) return 'Medium Usage (251-1250 Views)';
                    if (value === 3) return 'High Usage (1250+ Views)';
                    return 'No Usage';
                }
            };
        default:
            return getRangeConfig('Daily');
    }
};

interface ExtendedHeatMapWidgetProps extends HeatMapWidgetProps {
    timePeriod?: 'Daily' | 'Weekly' | 'Monthly';
}

const HeatMapWidget = ({
    isLoading = false,
    data,
    title,
    heightDifferencePercent = 0,
    legentText,
    children,
    xLabels: xLabelsProp,
    yLabels: yLabelsProp,
    timePeriod = 'Daily'
}: Readonly<ExtendedHeatMapWidgetProps>) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
    const [, setGridLeft] = useState<string>('5%');
    const [minChartHeight, setMinChartHeight] = useState<string>(getHeightPx(defaultHeight, heightDifferencePercent));
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [chartOption, setChartOption] = useState<any>({});

    useEffect(() => {
        // re-render chart to fix alignment when resizing the browser
        const handleResize = debounce(() => {
            if (chartContainerRef.current) {
                const containerWidth = chartContainerRef.current.offsetWidth;
                const calculatedLeft = getGridLeft(containerWidth);
                const calculatedChartHeight = getCalculatedChartHeight(
                    containerWidth,
                    heightDifferencePercent,
                    defaultHeight
                );
                setGridLeft(calculatedLeft);
                setMinChartHeight(calculatedChartHeight);
                setWindowSize(window.innerWidth);
            }
        }, 200);

        // Use actual data from API or fallback
        const yLabels: string[] = yLabelsProp && yLabelsProp.length > 0 ? yLabelsProp : ['17/09/25', '18/09/25', '19/09/25', '20/09/25', '21/09/25', '22/09/25', 'Today'];
        const xLabels: string[] = xLabelsProp && xLabelsProp.length > 0 ? xLabelsProp : ['12AM', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12PM', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];
        
        // Get range configuration based on time period
        const rangeConfig: RangeConfig = getRangeConfig(timePeriod);

        // **CRITICAL FIX**: Ensure data is properly formatted for ECharts
        const heatmapData = (data && Array.isArray(data) && data.length > 0) ? data : [];
        
        
        const chartOption = {
            backgroundColor: colors.widget?.background?.chart,
            tooltip: {
                position: 'top',
                backgroundColor: colors.widget?.chart?.tooltipBg,
                borderColor: colors.widget?.chart?.legendTextColor,
                borderWidth: 1,
                textStyle: {
                    color: colors.widget?.chart?.tooltipText,
                    fontSize: 12
                },
                formatter: function (params: any) {
                    const xLabel = xLabels[params.data[0]];
                    const yLabel = yLabels[params.data[1]];
                    const level = params.data[2];
                    const actualCount = params.data[3] || 0; // Get actual count from 4th element
                    
                    // Get the usage text based on level
                    let usageText: string;
                    if (level === 0) {
                        usageText = 'No Usage';
                    } else if (level === 1) {
                        usageText = `Low Usage (${actualCount} Views)`;
                    } else if (level === 2) {
                        usageText = `Medium Usage (${actualCount} Views)`;
                    } else if (level === 3) {
                        usageText = `High Usage (${actualCount} Views)`;
                    } else {
                        usageText = rangeConfig.tooltipFormatter(level);
                    }
                    
                    return `${yLabel} at ${xLabel}<br/>${usageText}`;
                }
            },
            grid: {
                height: '80%',
                top: '10%',
                right: '35%',
                left: windowSize < 768 ? '15%' : '2%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: xLabels,
                splitArea: { show: true },
                axisLabel: {
                    color: colors.widget?.chart?.labelColorSecondary,
                    fontSize: 10,
                    rotate: windowSize < 768 ? 45 : 0
                }
            },
            yAxis: {
                type: 'category',
                data: yLabels,
                splitArea: { show: true },
                axisLabel: {
                    color: colors.widget?.chart?.labelColorSecondary,
                    fontSize: 10,
                }
            },
            visualMap: {
                min: 0,
                max: 3,
                type: 'piecewise',
                orient: 'vertical',
                right: 10,
                top: 'center',
                calculable: false,
                dimension: 2, // Map to the 3rd element (index 2) which is the level
                pieces: rangeConfig.pieces.map(piece => ({
                    value: piece.value,
                    label: piece.label,
                    color: piece.color
                })),
                textStyle: {
                    fontSize: windowSize < 768 ? 8 : 10,
                    color: colors.widget?.chart?.legendTextColor,
                    align: 'left'
                },
                itemWidth: 20,
                itemHeight: 14,
                inRange: {
                    color: rangeConfig.pieces.map(p => p.color)
                }
            },
            // **CRITICAL FIX**: Add series configuration
            series: [
                {
                    name: 'Report Usage',
                    type: 'heatmap',
                    data: heatmapData,
                    label: {
                        show: false
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: colors.widget?.chart?.emptyCircleFill
                        }
                    }
                }
            ]
        };

        setChartOption(chartOption);
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [data, xLabelsProp, yLabelsProp, heightDifferencePercent, windowSize, timePeriod, colors]);
    
    return (
        <Box display="flex" flexGrow={1} height="100%" position="relative">
            <Stack
                minHeight="100px"
                p={2}
                bgcolor={colors.widget?.background?.chart}
                fontFamily="Montserrat"
                borderRadius="5px"
                flexGrow={1}
            >
                <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1}>
                    <Typography variant="widgetTitle" sx={{ color: colors.widget?.text?.title }}>{title}</Typography>
                    <Box display='flex' gap={1}>
                        {children}
                    </Box>
                </Stack>
                <Stack height={minChartHeight} flexGrow={1} justifyContent="center" key={windowSize} ref={chartContainerRef}>
                    {!isLoading ? (
                        <ReactECharts
                            option={chartOption}
                            style={{ width: '100%', height: '100%' }}
                            opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                            notMerge={true}
                            lazyUpdate={true}
                            onChartReady={() => {
                                console.log('✅ Heatmap chart rendered successfully')
                            }}
                        />
                    ) : (
                        <LoaderComponent />
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default HeatMapWidget;